import { loadBand, loadPct, type LoadBand } from '@rundown/domain';
import type { ClientStatus, SprintState } from '@rundown/types';

/**
 * The derivations behind the Sprints and Plan screens, kept out of JSX so they
 * can be tested and so both screens say the same thing about the same sprint.
 *
 * Everything takes `today` as an argument rather than reading the clock, for
 * the same reason @rundown/domain does: a test that depends on what day it is
 * run is a test that fails on a Monday.
 */

/**
 * What a sprint card calls itself. The database has four states; the timeline
 * shows five, because "the next sprint, which we confirm at planning" and "a
 * sprint after that, filled only by the ranking" are different promises
 * (Spec v0.5 §4 Sprints).
 */
export type TimelineState = 'released' | 'started' | 'locked' | 'planning' | 'projected';

export const TIMELINE_STATE_LABEL: Readonly<Record<TimelineState, string>> = {
  released: 'Released',
  started: 'Running',
  locked: 'Locked',
  planning: 'Planning',
  projected: 'Projected',
};

interface SprintLike {
  readonly id: string;
  readonly state: SprintState;
  readonly startsOn: string;
  readonly endsOn: string;
}

/** Sprints in display order in, one timeline state per sprint out. */
export function timelineStates<T extends SprintLike>(sprints: readonly T[]): Map<string, TimelineState> {
  const states = new Map<string, TimelineState>();
  let seenPlanning = false;
  for (const sprint of sprints) {
    if (sprint.state !== 'planning') {
      states.set(sprint.id, sprint.state);
    } else {
      states.set(sprint.id, seenPlanning ? 'projected' : 'planning');
      seenPlanning = true;
    }
  }
  return states;
}

/**
 * The sprint to land on when nobody has chosen one.
 *
 * The started sprint if there is one — that question now has an exact answer
 * rather than the date arithmetic this used to do, because starting is its own
 * act and at most one sprint may be running. Failing that, the soonest locked
 * sprint: nothing is running, but something is promised and about to.
 */
export function currentSprintId<T extends SprintLike>(sprints: readonly T[], today: string): string | null {
  const started = sprints.find((sprint) => sprint.state === 'started');
  if (started) return started.id;

  const locked = sprints.filter((sprint) => sprint.state === 'locked');
  const due = locked.find((sprint) => sprint.startsOn <= today);
  return due?.id ?? locked[0]?.id ?? null;
}

/** A locked sprint whose start date has arrived, and which nobody has started. */
export function isOverdueToStart<T extends SprintLike>(sprint: T, today: string): boolean {
  return sprint.state === 'locked' && sprint.startsOn <= today;
}

function toDate(isoDate: string): Date {
  return new Date(`${isoDate}T00:00:00Z`);
}

function isWorkingDay(date: Date): boolean {
  const day = date.getUTCDay();
  return day !== 0 && day !== 6;
}

/**
 * "Day 3 of 10" — working days, because nobody counts the weekend of a sprint.
 * Null outside the sprint's dates.
 */
export function workingDayOf(
  startsOn: string,
  endsOn: string,
  today: string,
): { day: number; of: number } | null {
  if (today < startsOn || today > endsOn) return null;
  let of = 0;
  let day = 0;
  for (
    let cursor = toDate(startsOn);
    cursor <= toDate(endsOn);
    cursor = new Date(cursor.getTime() + 86_400_000)
  ) {
    if (!isWorkingDay(cursor)) continue;
    of += 1;
    if (cursor.toISOString().slice(0, 10) <= today) day = of;
  }
  return of === 0 ? null : { day: Math.max(day, 1), of };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * "14 Sep", or "Mon 14 Sep" when `long`. By hand rather than Intl: the design
 * reads "Sep", and en-AU's Intl says "Sept" with a comma after the weekday —
 * and a sprint date should read the same in every browser.
 */
export function sprintDate(isoDate: string, long = false): string {
  const date = toDate(isoDate);
  const short = `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
  return long ? `${WEEKDAYS[date.getUTCDay()]} ${short}` : short;
}

/** "14 Sep – 18 Sep", or "Mon 14 Sep – Fri 18 Sep" when `long`. */
export function sprintRange(startsOn: string, endsOn: string, long = false): string {
  return `${sprintDate(startsOn, long)} – ${sprintDate(endsOn, long)}`;
}

export const LOAD_LABEL: Readonly<Record<LoadBand, string>> = {
  light: 'Light',
  loaded: 'Loaded',
  overloaded: 'Overloaded',
};

/** Band, label and a bar width clamped to 0–100, from the domain's own thresholds. */
export function loadInfo(
  hours: number,
  plannableHours: number,
): { band: LoadBand; label: string; width: number } {
  const band = loadBand(hours, plannableHours);
  const pct = loadPct(hours, plannableHours);
  return {
    band,
    label: LOAD_LABEL[band],
    width: Number.isFinite(pct) ? Math.min(Math.max(pct, 0), 100) : 100,
  };
}

/** Not started yet — what gets named first when hotfixes eat into a sprint (v0.3 §6.4 rule 6). */
const NOT_STARTED: ReadonlySet<ClientStatus> = new Set<ClientStatus>(['received', 'queued', 'scheduled']);

/**
 * "Items likely to carry over are named, with the cause" (v0.3 §6.4 rule 6).
 *
 * When hotfixes have used more than their reserve, the time came out of the
 * sprint, and what gives is the work nobody has started. Named last-first by
 * size: the biggest unstarted item is the one the overrun is most likely to
 * push out, so it is the honest one to put in front of the client.
 */
export function likelyToCarryOver<
  T extends { status: ClientStatus; removedAt: string | null; approxHours: number },
>(items: readonly T[], limit = 3): T[] {
  return (
    items
      .filter((item) => item.removedAt === null && NOT_STARTED.has(item.status))
      // eslint-disable-next-line unicorn/no-array-sort -- sorting the array `filter` just built
      .sort((a, b) => b.approxHours - a.approxHours)
      .slice(0, limit)
  );
}

/** Today as an ISO date in the viewer's own calendar — "this week" is the reader's week. */
export function localToday(now: Date = new Date()): string {
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}
